import {
  ANNOTATION_TYPE,
  BCC_LABELS,
  WIDGET_TYPE,
  getMeasurementFromSample,
} from '@/widgets/ecg-viewer';
import { zeroPad, dateToString } from '@/shared/lib';
import dayjs from 'dayjs';
import _ from 'lodash';

/**
 * De-interleaves a downloaded binary ECG buffer into one sample array per channel. Steps through the buffer in strides of the channel count pushing each sample onto its own channel array, and returns undefined when no buffer was supplied.
 *
 * @param totalChannel - Number of interleaved ECG channels.
 * @param binaryData - Interleaved binary ECG samples.
 * @returns One sample array per channel, or undefined without input data.
 */
const convertBinaryToECGData = (totalChannel: any, binaryData: any) => {
  if (binaryData) {
    const ecgData = new Array(totalChannel);
    for (let i = 0; i < binaryData.length; i += totalChannel) {
      for (let j = 0; j < totalChannel; j += 1) {
        if (ecgData[j]) {
          ecgData[j].push(binaryData[i + j]);
        } else {
          ecgData[j] = [binaryData[i + j]];
        }
      }
    }
    return ecgData;
  }
  return undefined;
};

/**
 * Parses a downloaded minutely heart-rate payload into numbers for the full-event chart. Splits the comma-separated string and maps every token through Number.
 *
 * @param minutelyHrs - Comma-separated minutely heart-rate payload.
 * @returns Heart-rate values, one per minute of the event.
 */
const calculateMinutelyHrsDataArray = (minutelyHrs: any) =>
  _.map(minutelyHrs.split(','), (x) => Number(x));

/**
 * Counts how many ECG samples fall inside the partial first minute of an event. Takes the seconds part of the first sample timestamp and multiplies the remainder of that minute by the sampling frequency.
 *
 * @param firstSample - Timestamp of the first ECG sample.
 * @param samplingFrequency - Samples per second.
 * @returns Sample count covering the rest of the first minute.
 */
const getFirstMinuteSampleLength = (firstSample: any, samplingFrequency: any) => {
  const firstSampleSecond = dayjs(firstSample).get('second');
  return (60 - firstSampleSecond) * samplingFrequency;
};

/**
 * Slices a full event's ECG channels into one chunk per minute so the viewer can page through the event minute by minute. Walks a sample cursor across every channel taking the first-minute remainder, a full 60-second window for middle minutes and the tail for the last one; when the first minute is shorter than a 10-second window it prepends alternating offset sine half-waves plus zero padding as filler and reports how many samples were added.
 *
 * @param fullECGData - Decoded ECG samples per channel for the whole event.
 * @param minutelyHrsLength - Number of minutes the event spans.
 * @param firstMinuteSampleLength - Sample count belonging to the partial first minute.
 * @param samplingFrequency - Samples per second.
 * @param adcGain - ADC gain scaling the sine-wave filler amplitude and offset.
 * @returns Per-minute ECG chunks together with the filler sample count.
 */
const calculateMinutelyHrsECGData = (
  fullECGData: any,
  minutelyHrsLength: any,
  firstMinuteSampleLength: any,
  samplingFrequency: any,
  adcGain: any,
): any => {
  const oneWindowSampleLength = 10 * samplingFrequency;
  const oneMinuteSampleLength = 60 * samplingFrequency;

  const minutelyHrsECGData = [];
  let pushedSampleCount = 0;

  let missingSampleLength = 0;

  for (let i = 0; i < minutelyHrsLength; i += 1) {
    const ecgData = [];

    for (let j = 0; j < fullECGData.length; j += 1) {
      if (i === 0) {
        // *: First minute
        let pushECGData: any = [];

        if (firstMinuteSampleLength < oneWindowSampleLength) {
          // *: Fill missing data to draw enough 10s

          let sineWaveDataArray = [];

          const halfSamplingFrequency = samplingFrequency / 2;

          missingSampleLength = oneWindowSampleLength - firstMinuteSampleLength;
          const sineWaveCount = Math.floor(missingSampleLength / samplingFrequency);
          const remainSampleLength = missingSampleLength - sineWaveCount * samplingFrequency;

          sineWaveDataArray = _.map(
            _.range(samplingFrequency),
            (x) => Math.sin(x * ((2 * Math.PI) / samplingFrequency)) * (0.75 * (adcGain / 2)),
          );

          // *: Create positive & negative sinewave with translating y-offset by 0.75 * adcGain (1.5 square)
          /**
           * First half of one sine-wave period shifted down by 1.25 times the ADC gain, used as filler for missing ECG samples. Computed by mapping the sine wave samples up to the half-sampling-frequency index and subtracting the offset.
           *
           * @returns Filler samples forming the positive sine half-wave.
           */
          const positiveSineWaveDataArray = _.map(
            sineWaveDataArray.slice(0, halfSamplingFrequency),
            (x) => x - 1.25 * adcGain,
          );
          /**
           * Second half of one sine-wave period shifted up by 1.25 times the ADC gain, used as filler for missing ECG samples. Computed by mapping the sine wave samples from the half-sampling-frequency index to the end and adding the offset.
           *
           * @returns Filler samples forming the negative sine half-wave.
           */
          const negativeSineWaveDataArray = _.map(
            sineWaveDataArray.slice(halfSamplingFrequency, sineWaveDataArray.length),
            (x) => x + 1.25 * adcGain,
          );

          // *: Each sineWaveCount has couple of positive & negative sinewave
          for (let k = 0; k < sineWaveCount; k += 1) {
            pushECGData = pushECGData.concat(positiveSineWaveDataArray);
            pushECGData = pushECGData.concat(negativeSineWaveDataArray);
          }

          if (remainSampleLength > 0) {
            if (remainSampleLength >= halfSamplingFrequency) {
              const remainZeroSampleLength = remainSampleLength % halfSamplingFrequency;
              // *: Concat one positive sinewave and the rest is zero sample
              pushECGData = pushECGData.concat(positiveSineWaveDataArray);
              pushECGData = pushECGData.concat(new Array(remainZeroSampleLength).fill(0));
            } else {
              // *: Fill the rest with zero sample
              pushECGData = pushECGData.concat(new Array(remainSampleLength).fill(0));
            }
          }

          pushECGData = pushECGData.concat(
            fullECGData[j].slice(pushedSampleCount, firstMinuteSampleLength),
          );
        } else {
          pushECGData = fullECGData[j].slice(pushedSampleCount, firstMinuteSampleLength);
        }
        ecgData.push(pushECGData);
      } else if (i === minutelyHrsLength - 1) {
        // *: Last minute
        const pushECGData = fullECGData[j].slice(pushedSampleCount, fullECGData[j].length);
        ecgData.push(pushECGData);
      } else {
        // *: Other minutes
        const pushECGData = fullECGData[j].slice(
          pushedSampleCount,
          pushedSampleCount + oneMinuteSampleLength,
        );
        ecgData.push(pushECGData);
      }
    }

    if (i === 0) {
      pushedSampleCount += firstMinuteSampleLength;
    } else {
      pushedSampleCount += ecgData[0].length;
    }
    minutelyHrsECGData.push(ecgData);
  }

  return { minutelyHrsECGData, missingSampleLength };
};

/**
 * Upgrades a saved version-1 ECG viewer object so its annotations can be drawn by the current chart. Deep-clones the object and, for anything not already tagged v2, rewrites each widget into an annotation: horizontal and vertical calipers, text, beat-complex calipers with their BCC labels and QRS measurement string, section sample marks and the event position, while a v2 object passes through untouched.
 *
 * @param viewerObject - Saved viewer object holding its widgets and version.
 * @param samplingFrequency - Samples per second, used to turn caliper samples into a measurement.
 * @returns Cloned viewer object whose widgets are annotation entries.
 */
const convertViewerObject = (viewerObject: any, samplingFrequency: any): any => {
  const clonedViewerObject = _.cloneDeep(viewerObject);
  const convertedWidgets: any = [];
  const { widgets, version } = clonedViewerObject;
  if (version !== 'v2') {
    _.forEach(widgets, (widget) => {
      switch (widget.type) {
        case WIDGET_TYPE.HORIZONTAL_CALIPER: {
          convertedWidgets.push({
            type: ANNOTATION_TYPE.HLINE,
            channelIndex: widget.chIdx,
            length: getMeasurementFromSample(
              widget.endSample,
              widget.startSample,
              samplingFrequency,
            ),
            xr: widget.startSample,
            yr: widget.startVoltage,
            belowText: widget.belowText || '',
            colorHLine: widget.colorHLine || '#0000FF',
          });
          break;
        }
        case WIDGET_TYPE.VERTICAL_CALIPER: {
          convertedWidgets.push({
            type: ANNOTATION_TYPE.VLINE,
            channelIndex: widget.chIdx,
            xr: widget.startSample,
            yr1: widget.startVoltage,
            yr2: widget.endVoltage,
            colorVLine: widget.colorVLine || '#0000FF',
          });
          break;
        }
        case WIDGET_TYPE.TEXT_ANNOTATION: {
          convertedWidgets.push({
            type: ANNOTATION_TYPE.TEXT,
            channelIndex: widget.chIdx,
            xr: widget.startSample,
            yr: widget.startVoltage,
            text: widget.text,
          });
          break;
        }
        case WIDGET_TYPE.BEAT_COMPLEX_CALIPERS: {
          const xr = {};
          _.forEach(_.keys(widget.bccSamples), (key) => {
            _.assign(xr, { [(BCC_LABELS as any)[key]]: widget.bccSamples[key] });
          });
          convertedWidgets.push({
            type: ANNOTATION_TYPE.BCC,
            channelIndex: widget.chIdx,
            xr,
            text: ` Rate: ${widget.QRSMeasurements.rate} PR: ${widget.QRSMeasurements.pr} QRS: ${widget.QRSMeasurements.qrs} QT: ${widget.QRSMeasurements.qt} QTc: ${widget.QRSMeasurements.qtc} `,
          });
          break;
        }
        case WIDGET_TYPE.SECTION: {
          convertedWidgets.push({
            type: ANNOTATION_TYPE.SAMPLE_MARK,
            channelIndex: widget.chIdx,
            startSample: widget.startSample,
            endSample: widget.endSample,
          });
          break;
        }
        case WIDGET_TYPE.EVENT_POSITION: {
          convertedWidgets.push({
            type: ANNOTATION_TYPE.EVENT_POSITION,
            position: widget.startSample,
          });
          break;
        }
        default: {
          break;
        }
      }
    });
    _.assign(clonedViewerObject, { widgets: convertedWidgets });
  }
  return clonedViewerObject;
};

/**
 * Builds the header blocks shown above an event's ECG strip. Returns the zero-padded event id, the event type and the trigger time rendered against the study start and the facility timezone, or an empty list when no title info is present.
 *
 * @param titleInfo - Event id, type, trigger time and timezone information for the header.
 * @returns Title and content pairs for the viewer header, empty when info is missing.
 */
const generateTitleBlockArray = (titleInfo: any) =>
  !_.isEmpty(titleInfo)
    ? [
        {
          title: 'Event ID',
          content: zeroPad(titleInfo.eventFid),
        },
        {
          title: 'Event type',
          content: titleInfo.eventType,
        },
        {
          title: 'Trigger time',
          content: dateToString({
            startStudyDate: titleInfo.startStudyDate,
            isoDate: titleInfo.triggerTime,
            timezone: titleInfo.timezone,
            studyTimeOffset: titleInfo.studyTimeOffset,
            facilityCountry: titleInfo.facilityCountry,
            facilityTimezone: titleInfo.facilityTimezone,
          }),
        },
      ]
    : [];

export {
  convertBinaryToECGData,
  calculateMinutelyHrsDataArray,
  getFirstMinuteSampleLength,
  calculateMinutelyHrsECGData,
  convertViewerObject,
  generateTitleBlockArray,
};
